/**
 * Premium emoji — Telegram custom (animatsion) emojilar qatlami.
 *
 * Matn formati Telegramning o'zi tushunadigan HTML tegi:
 *   <tg-emoji emoji-id="5909085768990594843">🤩</tg-emoji>
 * Bot xabarni parse_mode=HTML bilan yuborganda premium emoji ko'rinadi,
 * premium bo'lmagan foydalanuvchi esa fallback belgini (🤩) ko'radi.
 *
 * Komponentlar:
 *   PremiumEmoji  — bitta emojini jonli ko'rsatadi (lottie/webm/webp)
 *   EmojiTextarea — textarea o'rnini bosuvchi contenteditable maydon:
 *                   tg-emoji teglar jonli emoji bo'lib ko'rinadi, saqlashda
 *                   yana teg ko'rinishiga qaytadi
 *   stripTgEmoji  — teglarni fallback belgiga almashtiradi (preview uchun)
 *
 * Tashqi bog'liqlik: ixtiyoriy `lottie-web` (faqat lottie formatga kerak,
 * dinamik import — o'rnatilmagan bo'lsa fallback belgi qoladi).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { HiFaceSmile } from 'react-icons/hi2'
import { cx } from './primitives.jsx'

// emoji obyekti shartnomasi (backend /emojis bilan bir xil):
//   { custom_emoji_id, base_emoji, format: 'lottie'|'webm'|'webp', file_url }

const TG_EMOJI_SRC = '<tg-emoji\\s+emoji-id="(\\d+)"\\s*>([\\s\\S]*?)</tg-emoji>'

/** Preview/karta uchun: <tg-emoji ...>🤩</tg-emoji> → 🤩 */
export function stripTgEmoji(text) {
  return String(text ?? '').replace(new RegExp(TG_EMOJI_SRC, 'g'), '$2')
}

// ── lottie-web va animatsiya JSON keshlari ──────────────────
let lottieLib // undefined=hali, null=yo'q, obyekt=tayyor
let lottiePromise = null
function loadLottie() {
  if (lottieLib !== undefined) return Promise.resolve(lottieLib)
  lottiePromise ??= import('lottie-web')
    .then(m => { lottieLib = m.default || m; return lottieLib })
    .catch(() => { lottieLib = null; return null })
  return lottiePromise
}

const animCache = new Map() // file_url -> Promise<json|null>
function loadAnimData(url) {
  if (!animCache.has(url)) {
    animCache.set(url, fetch(url).then(r => (r.ok ? r.json() : null)).catch(() => null))
  }
  return animCache.get(url)
}

// Harakatni kamaytirish so'ralgan bo'lsa animatsiya umuman yuklanmaydi —
// chipda oddiy fallback belgi qoladi (bo'sh katak emas).
const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// ── Bitta chip elementini jonlantirish (oddiy DOM, React'siz —
//    contenteditable ichida ham, PremiumEmoji ichida ham ishlaydi) ──
function hydrateChip(el) {
  if (!el || el.dataset.live) return
  el.dataset.live = '1'
  const { fmt, src } = el.dataset
  if (!src) return // fayl yo'q — fallback belgi qoladi
  if (fmt !== 'webp' && reducedMotion()) return
  if (fmt === 'webp') {
    const img = new Image()
    img.alt = el.dataset.fallback || ''
    img.draggable = false
    img.onload = () => { el.textContent = ''; el.appendChild(img) }
    img.src = src
  } else if (fmt === 'webm') {
    const v = document.createElement('video')
    v.muted = true; v.loop = true; v.autoplay = true; v.playsInline = true
    v.setAttribute('muted', ''); v.setAttribute('playsinline', '')
    v.onerror = () => {} // fallback belgi qoladi
    v.oncanplay = () => {
      if (v.isConnected) return
      el.textContent = ''
      el.appendChild(v)
      v.play().catch(() => {})
    }
    v.src = src
  } else {
    // lottie (tgs → json)
    Promise.all([loadLottie(), loadAnimData(src)]).then(([lottie, data]) => {
      if (!lottie || !data || !el.isConnected) return
      const holder = document.createElement('span')
      holder.className = 'ui-emoji-chip__anim'
      try {
        el.textContent = ''
        el.appendChild(holder)
        el._anim = lottie.loadAnimation({
          container: holder, renderer: 'svg', loop: true, autoplay: true, animationData: data,
        })
      } catch {
        // Buzuq animatsiya — katak bo'sh qolmasin, fallback belgi qaytadi
        el.textContent = el.dataset.fallback || '⭐'
      }
    })
  }
}

function destroyChips(container) {
  container.querySelectorAll('.ui-emoji-chip').forEach(el => {
    try { el._anim?.destroy() } catch { /* allaqachon yo'q */ }
  })
}

function makeChip(em) {
  const el = document.createElement('span')
  el.className = 'ui-emoji-chip'
  el.contentEditable = 'false'
  el.dataset.ceid = String(em.custom_emoji_id)
  el.dataset.fallback = em.base_emoji || '⭐'
  el.dataset.fmt = em.format || 'lottie'
  if (em.file_url) el.dataset.src = em.file_url
  el.textContent = em.base_emoji || '⭐'
  return el
}

// ── PremiumEmoji — bitta jonli emoji ────────────────────────
/** `emoji` — kutubxona yozuvi; `size` px, belgi o'lchami ham shunga ergashadi. */
export function PremiumEmoji({ emoji, size = 20, className, ...rest }) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    // qayta ishlatishда (ro'yxat almashsa) chipni tozalab qaytadan jonlantiramiz
    delete el.dataset.live
    el.textContent = emoji?.base_emoji || '⭐'
    el.dataset.ceid = String(emoji?.custom_emoji_id || '')
    el.dataset.fallback = emoji?.base_emoji || '⭐'
    el.dataset.fmt = emoji?.format || 'lottie'
    if (emoji?.file_url) el.dataset.src = emoji.file_url; else delete el.dataset.src
    hydrateChip(el)
    return () => { try { el._anim?.destroy() } catch { /* yo'q */ } }
  }, [emoji?.custom_emoji_id, emoji?.file_url, emoji?.format, emoji?.base_emoji])
  return (
    <span
      ref={ref}
      className={cx('ui-emoji-chip', className)}
      style={{ fontSize: size * 0.8, width: size, height: size }}
      {...rest}
    />
  )
}

// ── value ↔ DOM aylantirish ────────────────────────────────
function escHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}
function escAttr(s) {
  return escHtml(s).replace(/"/g, '&quot;')
}

function chipHtml(ceid, fallback, em) {
  const src = em?.file_url ? ` data-src="${escAttr(em.file_url)}"` : ''
  const fmt = em?.format || 'lottie'
  return `<span class="ui-emoji-chip" contenteditable="false" data-ceid="${escAttr(ceid)}"` +
         ` data-fallback="${escAttr(fallback)}" data-fmt="${escAttr(fmt)}"${src}>${escHtml(fallback)}</span>`
}

function valueToHtml(value, emojiById) {
  const s = String(value ?? '')
  let html = ''
  let last = 0
  for (const m of s.matchAll(new RegExp(TG_EMOJI_SRC, 'g'))) {
    html += escHtml(s.slice(last, m.index))
    const em = emojiById.get(m[1])
    html += chipHtml(m[1], m[2] || em?.base_emoji || '⭐', em)
    last = m.index + m[0].length
  }
  html += escHtml(s.slice(last))
  return html
}

const NODE_TEXT = 3
const NODE_ELEMENT = 1

function serializeNode(node) {
  let out = ''
  node.childNodes.forEach(n => {
    if (n.nodeType === NODE_TEXT) { out += n.nodeValue; return }
    if (n.nodeType !== NODE_ELEMENT) return
    if (n.classList?.contains('ui-emoji-chip')) {
      out += `<tg-emoji emoji-id="${n.dataset.ceid}">${n.dataset.fallback || '⭐'}</tg-emoji>`
    } else if (n.tagName === 'BR') {
      out += '\n'
    } else {
      // brauzer qo'shgan div/p — yangi qatordan boshlanadi
      if (out && !out.endsWith('\n')) out += '\n'
      out += serializeNode(n)
    }
  })
  return out
}

// ── Tanlagich joylashuvi ────────────────────────────────────
// Tanlagich modal ichida ochiladi: absolyut joylashuvda uni modal tanasi
// (overflow-y: auto) qirqib qo'yadi yoki ekrandan pastga chiqarib yuboradi.
// Shuning uchun portal + position: fixed — tugmaning ekrandagi haqiqiy
// o'rniga tayanib qo'yiladi, pastda joy bo'lmasa TEPAGA ochiladi.
const PICKER_W = 300      // 8 ustun × 32px + oraliqlar + ichki bo'shliq
const PICKER_MAX_H = 208
const PICKER_MIN_H = 96
const PICKER_GAP = 6
const EDGE = 8            // ekran chetidan eng kam masofa

function pickerPosition(btn) {
  const r = btn.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight
  const below = vh - r.bottom - PICKER_GAP - EDGE
  const above = r.top - PICKER_GAP - EDGE
  // Pastda to'liq sig'masa-yu, tepada joy ko'proq bo'lsa — tepaga ochamiz
  const up = below < PICKER_MAX_H && above > below
  const room = Math.max(up ? above : below, PICKER_MIN_H)
  const maxHeight = Math.min(PICKER_MAX_H, room)
  // O'ng cheti tugma bilan tekislanadi, lekin ekrandan chiqib ketmaydi
  const left = Math.min(Math.max(EDGE, r.right - PICKER_W), Math.max(EDGE, vw - PICKER_W - EDGE))
  const top = up ? Math.max(EDGE, r.top - PICKER_GAP - maxHeight) : r.bottom + PICKER_GAP
  return { left, top, maxHeight }
}

// ── EmojiTextarea ───────────────────────────────────────────
/**
 * Textarea o'rnini bosuvchi maydon: matn + jonli premium emoji.
 * `value` — <tg-emoji> teglari bilan oddiy satr (Template.body_* bilan bir xil).
 * `emojis` — kutubxona ro'yxati (backend /emojis javobi, file_url to'liq URL).
 * Kutubxona bo'sh bo'lsa ham maydon oddiy matn bilan ishlayveradi.
 */
export function EmojiTextarea({
  value, onChange, emojis = [], rows = 4, placeholder, invalid = false,
  disabled = false, className, ...rest
}) {
  const ref = useRef(null)
  const wrapRef = useRef(null)
  const btnRef = useRef(null)
  const pickerRef = useRef(null)
  const lastValue = useRef(null)
  const lastMap = useRef(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null)   // {left, top, maxHeight} — portal uchun
  const emojiById = useMemo(
    () => new Map((emojis || []).map(e => [String(e.custom_emoji_id), e])),
    [emojis]
  )

  // Tashqi value o'zgarganda (modal ochilishi, form reset) DOM qayta quriladi.
  // O'zimiz emit qilgan value uchun qayta qurmaymiz — kursor sakramasin.
  //
  // Kutubxona kechroq kelishi mumkin (emojisApi.list() so'rovi maydon
  // chizilgandan keyin tugaydi) — bunda value o'zgarmagan bo'lsa ham qayta
  // quramiz, aks holda chiplar file_url siz qolib, jonlanmaydi. Kursor
  // sakramasligi uchun buni faqat maydonда fokus yo'q payti qilamiz.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const v = String(value ?? '')
    const mapChanged = lastMap.current !== emojiById
    if (v === lastValue.current && !(mapChanged && document.activeElement !== el)) return
    destroyChips(el)
    el.innerHTML = valueToHtml(v, emojiById)
    el.querySelectorAll('.ui-emoji-chip').forEach(hydrateChip)
    lastValue.current = v
    lastMap.current = emojiById
  }, [value, emojiById])

  useEffect(() => () => { if (ref.current) destroyChips(ref.current) }, [])

  // Tanlagich tashqarisiga bosilganda yopiladi. Tanlagich portalda —
  // maydon o'ramining ichida emas, shuning uchun ikkalasi ham tekshiriladi.
  useEffect(() => {
    if (!open) return
    const close = e => {
      if (wrapRef.current?.contains(e.target) || pickerRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  // Joylashuvni ochilishда hisoblaymiz va skroll/o'lcham o'zgarishida yangilaymiz.
  // Skroll `capture` bilan ushlanadi — modal tanasi kabi ichki skrollerlar
  // hodisani yuqoriga uzatmaydi.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return undefined
    const place = () => { if (btnRef.current) setPos(pickerPosition(btnRef.current)) }
    place()
    window.addEventListener('resize', place)
    document.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      document.removeEventListener('scroll', place, true)
    }
  }, [open])

  const emit = () => {
    const v = serializeNode(ref.current)
    lastValue.current = v
    onChange?.(v)
  }

  const handleKeyDown = e => {
    // Enter'ni oddiy \n ga aylantiramiz — brauzer div/br aralashmasin
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
      document.execCommand('insertText', false, '\n')
    }
  }
  const handlePaste = e => {
    e.preventDefault()
    const t = e.clipboardData?.getData('text/plain') || ''
    document.execCommand('insertText', false, t)
  }

  const insertEmoji = em => {
    const el = ref.current
    if (!el) return
    el.focus()
    const chip = makeChip(em)
    const sel = window.getSelection()
    if (sel?.rangeCount && el.contains(sel.anchorNode)) {
      const range = sel.getRangeAt(0)
      range.deleteContents()
      range.insertNode(chip)
      range.setStartAfter(chip)
      range.collapse(true)
      sel.removeAllRanges()
      sel.addRange(range)
    } else {
      el.appendChild(chip)
    }
    hydrateChip(chip)
    emit()
    setOpen(false)
  }

  return (
    <div ref={wrapRef} className={cx('ui-emoji-area', className)}>
      <div
        ref={ref}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-invalid={invalid || undefined}
        data-placeholder={placeholder || ''}
        className={cx('ui-textarea', 'ui-emoji-area__edit', invalid && 'is-invalid')}
        style={{ minHeight: `calc(${rows} * 1.55em + 18px)` }}
        onInput={emit}
        onBlur={emit}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        {...rest}
      />
      <button
        ref={btnRef}
        type="button"
        className="ui-emoji-area__btn"
        title="Premium emoji qo'shish"
        aria-label="Premium emoji qo'shish"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
      >
        <HiFaceSmile aria-hidden="true" />
      </button>
      {open && pos && createPortal(
        <div
          ref={pickerRef}
          className="ui-emoji-area__picker"
          role="listbox"
          aria-label="Premium emojilar"
          style={{ left: pos.left, top: pos.top, maxHeight: pos.maxHeight, width: PICKER_W }}
        >
          {(emojis || []).length === 0 ? (
            <div className="ui-emoji-area__empty">
              Kutubxona bo'sh. Botga premium emoji yuboring
              (<code>/emojiid</code>) — shu yerda paydo bo'ladi.
            </div>
          ) : (
            emojis.map(e => (
              <button
                key={e.custom_emoji_id}
                type="button"
                className="ui-emoji-area__cell"
                title={e.base_emoji}
                onClick={() => insertEmoji(e)}
              >
                <PremiumEmoji emoji={e} size={26} />
              </button>
            ))
          )}
        </div>,
        document.body,
      )}
    </div>
  )
}
